import { bundleInputSchema, type BundleDetail, type BundleInput } from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import type { JSX } from 'react';
import { useFieldArray, useForm, type Resolver } from 'react-hook-form';
import type { z } from 'zod';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { Label } from '@/components/ui/label.js';
import { Switch } from '@/components/ui/switch.js';
import { BundleComponentsTable } from './bundle-components-table.js';

/**
 * Formulaire d'un bundle (`02` §8), sur sa page dédiée — plus de dialogue, et
 * plus de section « surcharges par boutique » : un bundle a une seule
 * composition (décision du 10/09/2026).
 *
 * `bundleInputSchema` (`@flux/shared`) couvre déjà « au moins un composant » et
 * « quantités > 0 ». Il ne manque que « pas de composition réduite au bundle
 * lui-même » (`02` §8), contextuel au SKU — ajouté ici pour un message par
 * ligne, le serveur revalidant de toute façon en dernier ressort. Le bundle
 * peut figurer parmi ses composants à côté d'autres SKU (produit expédié avec
 * ses compléments).
 */
const bundleFormSchema = bundleInputSchema.superRefine((data, ctx) => {
  const selfOnly = data.sku !== '' && data.components.every((component) => component.sku === data.sku);
  if (!selfOnly) {
    return;
  }
  data.components.forEach((_component, index) => {
    ctx.addIssue({
      code: 'custom',
      message: 'Un bundle ne peut pas se composer uniquement de lui-même : ajoutez au moins un autre composant.',
      path: ['components', index, 'sku'],
    });
  });
});

export type BundleFormValues = z.infer<typeof bundleFormSchema>;

export interface BundleFormProps {
  /** `null` = création ; sinon le bundle à modifier (son SKU devient non éditable). */
  initial: BundleDetail | null;
  isSaving: boolean;
  /** Message d'erreur serveur à afficher sous le formulaire. */
  errorMessage?: string | undefined;
  onSubmit: (input: BundleInput) => void;
  onCancel: () => void;
}

export function BundleForm({
  initial,
  isSaving,
  errorMessage,
  onSubmit,
  onCancel,
}: BundleFormProps): JSX.Element {
  const isEditing = initial !== null;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<BundleFormValues>({
    resolver: zodResolver(bundleFormSchema) as Resolver<BundleFormValues>,
    // `values` : le formulaire repart de la version enregistrée dès qu'elle
    // change — « Enregistrer » redevient inactif une fois la sauvegarde faite.
    values: initial
      ? {
          sku: initial.sku,
          label: initial.label ?? '',
          enabled: initial.enabled,
          components: initial.components,
        }
      : {
          sku: '',
          label: '',
          enabled: true,
          components: [{ sku: '', qty: 1, unitPrice: null, order: 0 }],
        },
  });

  const fieldArray = useFieldArray({ control, name: 'components' });
  const enabled = watch('enabled');

  return (
    <form
      className="flex flex-col gap-6"
      noValidate
      onSubmit={(event) => {
        void handleSubmit((values) => {
          // `order` est réattribué à la position affichée : c'est l'ordre des
          // lignes dans le CSV (`01` §2.4).
          onSubmit({
            ...values,
            components: values.components.map((component, index) => ({
              ...component,
              order: index,
            })),
          });
        })(event);
      }}
    >
      <PageSection title="Identité">
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="SKU du bundle"
              htmlFor="bundle-sku"
              hint="Le SKU vendu sur Shopify. Il n’est plus modifiable une fois le bundle créé."
              error={errors.sku?.message}
            >
              <Input
                id="bundle-sku"
                disabled={isEditing}
                aria-invalid={errors.sku ? true : undefined}
                {...register('sku')}
              />
            </Field>
            <Field
              label="Libellé (optionnel)"
              htmlFor="bundle-label"
              hint="Affiché à la place du SKU dans les écrans, ex. « Crème x4 ». Laisser vide pour afficher le SKU."
              error={errors.label?.message}
            >
              <Input
                id="bundle-label"
                aria-invalid={errors.label ? true : undefined}
                {...register('label')}
              />
            </Field>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="bundle-enabled"
              checked={enabled}
              onCheckedChange={(checked) => {
                setValue('enabled', checked, { shouldDirty: true });
              }}
            />
            <Label htmlFor="bundle-enabled">Bundle actif</Label>
          </div>
        </div>
      </PageSection>

      <BundleComponentsTable register={register} fieldArray={fieldArray} errors={errors} />

      {errorMessage === undefined ? null : (
        <p role="alert" className="text-sm text-status-red-fg">
          {errorMessage}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <SaveButton isLoading={isSaving} isDirty={!isEditing || isDirty}>
          {isEditing ? 'Enregistrer' : 'Créer le bundle'}
        </SaveButton>
      </div>
    </form>
  );
}
